import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { ErrorHandlerService } from '../../../../../shared/services/errorHandling.service';
import { FileMetadataDto } from '../../dto/file-metadata.dto';
import { FcpDataDTO } from '../helpers/export.helper';
import { ExportingService } from '../service/fcp-exporting.service';
import { FinancialClosingProcessService } from '../service/fcp.service';
import * as fromActions from './fcp.actions';
import { selectActiveTestGroupItem, selectActiveTestItem, selectNavigationItems } from './fcp.reducer';

const debug = Debug('aumico:reports-fe:FcpEffects');

@Injectable()
export class FcpEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly financialClosingFileService: FinancialClosingProcessService,
        private readonly exportingService: ExportingService,
        private readonly errorHandlerService: ErrorHandlerService,
    ) {}

    setNavigationItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setNavigationItems),
            map((action) => {
                let navigationItems = action.payload;

                if (navigationItems && navigationItems.length > 0) {
                    // Check if any item is already active
                    const activeItemIndex = navigationItems.findIndex((item) => item.active);

                    // Set all items to active: false
                    navigationItems = navigationItems.map((item) => ({ ...item, active: false }));
                    if (activeItemIndex > -1) {
                        // If an active item exists, keep it active
                        navigationItems[activeItemIndex].active = true;
                    } else {
                        // If no active item exists, set the first item to active: true
                        navigationItems[0].active = true;
                    }

                    // Dispatch the updated navigation items
                    this.store.dispatch(fromActions.setNavigationItemsSuccess({ payload: navigationItems }));

                    // Dispatch the active navigation action for the active item
                    const activeItem = navigationItems.find((item) => item.active);
                    return fromActions.setActiveNavigation({ payload: activeItem });
                }

                // Dispatch the updated navigation items
                this.store.dispatch(fromActions.setNavigationItemsSuccess({ payload: navigationItems }));
                return fromActions.setActiveNavigation({ payload: undefined });
            }),
        ),
    );

    setActiveNavigation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setActiveNavigation),
            withLatestFrom(this.store.select(selectNavigationItems)), // Select the current navigation items state
            map(([action, navigationItems]) => {
                // Set all items to active: false
                const updatedNavigationItems = navigationItems.map((item) => ({ ...item, active: false }));

                // Set the specific item to active: true
                const activeItem = { ...action.payload, active: true };
                const index = updatedNavigationItems.findIndex((item) => item.uid === activeItem.uid);
                if (index > -1) {
                    updatedNavigationItems[index] = activeItem;
                }

                // Dispatch the updated navigation items
                this.store.dispatch(fromActions.setNavigationItemsSuccess({ payload: updatedNavigationItems }));
                this.store.dispatch(fromActions.toggleActiveTestGroupItemSuccess({ payload: undefined }));
                this.store.dispatch(fromActions.setActiveTestItemSuccess({ payload: undefined }));

                return fromActions.setActiveNavigationSuccess({ payload: activeItem });
            }),
        ),
    );

    setActiveTestGroupItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.toggleActiveTestGroupItem),
            withLatestFrom(this.store.select(selectActiveTestGroupItem), this.store.select(selectActiveTestItem)),
            map(([action, lastActive, activeTestItem]) => {
                if (activeTestItem) {
                    this.store.dispatch(fromActions.toggleActiveTestItem({ payload: activeTestItem }));
                }
                if (lastActive) {
                    const inactiveItem = { ...lastActive, active: false };
                    this.store.dispatch(fromActions.setFcpItemToReset({ payload: inactiveItem }));
                }
                if (lastActive && lastActive.uid === action.payload.uid) {
                    return fromActions.toggleActiveTestGroupItemSuccess({ payload: undefined });
                }

                const activeItem = { ...action.payload, active: true };
                return fromActions.toggleActiveTestGroupItemSuccess({ payload: activeItem });
            }),
        ),
    );

    setActiveTestItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.toggleActiveTestItem),
            withLatestFrom(this.store.select(selectActiveTestItem)),
            map(([action, lastActive]) => {
                if (lastActive) {
                    const inactiveItem = { ...lastActive, active: false };
                    this.store.dispatch(fromActions.setFcpItemToReset({ payload: inactiveItem }));
                }
                if (lastActive && lastActive.uid === action.payload.uid) {
                    return fromActions.setActiveTestItemSuccess({ payload: undefined });
                }
                const activeItem = { ...action.payload, active: true };
                return fromActions.setActiveTestItemSuccess({ payload: activeItem });
            }),
        ),
    );

    unsetActiveTestItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.unsetActiveTestItem),
            withLatestFrom(this.store.select(selectActiveTestItem)),
            map(([action, lastActive]) => {
                if (lastActive) {
                    const inactiveItem = { ...lastActive, active: false };
                    this.store.dispatch(fromActions.setFcpItemToReset({ payload: inactiveItem }));
                }

                return fromActions.setActiveTestItemSuccess({ payload: undefined });
            }),
        ),
    );

    downloadFinancialClosingProcessItemDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.financialClosingProcessItemDocument.download.type),
            mergeMap(({ documentName, documentId }) => {
                return this.financialClosingFileService.downloadDocument$(documentId, documentName).pipe(
                    tap((blob) => {
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const downloadedLink = document.createElement('a');
                        downloadedLink.href = downloadUrl;
                        downloadedLink.download = documentName;
                        document.body.appendChild(downloadedLink);
                        downloadedLink.click();

                        document.body.removeChild(downloadedLink);
                        window.URL.revokeObjectURL(downloadUrl);
                    }),
                    map(() => {
                        return fromActions.financialClosingProcessItemDocument.downloadsuccess();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === HttpStatusCode.UnprocessableEntity) {
                            this.errorHandlerService.openErrorDialog(
                                'aumico_reports_fcp_file_is_virused_warning_popup',
                            );
                        }
                        return of(fromActions.financialClosingProcessItemDocument.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    deleteFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.financialClosingProcessItemDocument.deletefcpdocuments.type),
            mergeMap(({ payload }) => {
                return this.financialClosingFileService.deleteDocument$(payload).pipe(
                    map(() => {
                        return fromActions.financialClosingProcessItemDocument.deletefcpdocumentsucces();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.financialClosingProcessItemDocument.deletefcpdocumentfailed({ payload: error }),
                        );
                    }),
                );
            }),
        ),
    );

    exportingFcpData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportingFcpData.exportingFcpData.type),
            mergeMap((action: fromActions.ActionWithPayload<{ data: FcpDataDTO; fileName: string }>) => {
                return this.exportingService.exportFcpData$(action.payload.data).pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        });
                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(blob);
                        downloadLink.download = action.payload.fileName + '.docx';
                        downloadLink.click();
                        return fromActions.exportingFcpData.exportingFcpDataSuccess();
                    }),
                    catchError((error) => {
                        return of(fromActions.exportingFcpData.exportingFcpDataFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    uploadFinancialClosingProcessItemDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.financialClosingProcessItemDocument.do.type),
            mergeMap(({ file }) => {
                return this.financialClosingFileService.uploadFinancialClosingProcessDocument$(file).pipe(
                    mergeMap((data) => {
                        const payload: Partial<FileMetadataDto> = {
                            uuid: data.uuid,
                            name: data.name,
                            createdBy: data.createdBy,
                            scanStatus: data.scanStatus,
                            size: data.size,
                        };

                        return of(fromActions.financialClosingProcessItemDocument.success({ payload }));
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.financialClosingProcessItemDocument.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    loadFcpChecklists$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getFcpChecklist.loadChecklists.type),
            mergeMap(() =>
                this.financialClosingFileService.getFcpChecklists$().pipe(
                    map((checklists) => fromActions.getFcpChecklist.loadChecklistsSuccess({ checklists })),
                    catchError((error) => of(fromActions.getFcpChecklist.loadChecklistsFailure({ error }))),
                ),
            ),
        ),
    );
}
